import type { Event } from "@/types";
import { Text } from "@components/atoms";
import { MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EventCardSecondaryProps {
  event: Event;
}

/**
 * Secondary event card molecule.
 * Smaller card (160×247) used in grids and search results.
 * Clicks through to the event detail page.
 */
export function EventCardSecondary({ event }: EventCardSecondaryProps) {
  const navigate = useNavigate();
  const eventKey = event.slug?.trim() || event.id;

  return (
    <article
      className="w-full overflow-hidden rounded-[9px] bg-neutral-50 cursor-pointer transition-transform hover:scale-[1.02]"
      onClick={() => navigate(`/events/${encodeURIComponent(eventKey)}`)}
    >
      {/* Event image */}
      <div className="overflow-hidden w-full" style={{ aspectRatio: "160/150" }}>
        {event.image ? (
          <img
            src={event.image}
            alt={event.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-100/20">
            <Text variant="body-sm" className="text-white/40">
              Sin imagen
            </Text>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 p-3">
        {/* Event name */}
        <div style={{ minHeight: 35 }}>
          <Text
            variant="body"
            as="h4"
            className="text-white font-medium leading-tight line-clamp-2"
            style={{ fontSize: 14 }}
            title={event.name}
          >
            {event.name}
          </Text>
        </div>

        {/* Location */}
        {(event.location.venue || event.location.city) && (
          <div className="mt-1 flex items-center gap-1">
            <MapPin size={12} className="flex-shrink-0 text-neutral-30" />
            <Text variant="body-sm" as="span" className="text-neutral-30">
              {event.location.venue
                ? event.location.venue
                : (event.location.city && event.location.country
                    ? `${event.location.city}, ${event.location.country}`
                    : event.location.city || event.location.country || "Ecuador")}
            </Text>
          </div>
        )}
      </div>
    </article>
  );
}
